const PGAdapter = require('../common/pg-adapter')
const isPGUsageErrorCode = require('../common/isPGUsageErrorCode')
const { getPositionalArgs } = require('../tools/sql-tools')
const LocationChangeAdapter = require('./data-access/location-change-adapter')

// The location fields that live in `data_location_options` rather than on the
// location row itself — the `additionalData` entries that never got a column
// here: warehouse_code (the PSM facility code), ward, facility_id, has_pcr and
// friends. Kept in one place so the select list below and callers agree.
const LOCATION_OPTIONS_COLUMNS = [
  'has_pcr',
  'facility_id',
  'ward',
  'tracks_partner_balances',
  'warehouse_code',
  'supplies_cold_chain',
  'alias',
  'batch_tracking_services'
]

// A location row with its options joined on, flat.
//
// A table expression rather than the adapter's `getRelated` mechanism, so the
// columns land directly on each row in a single query instead of arriving as a
// nested one-element array.
//
// The select list is explicit rather than `location.*, options.*` because both
// tables carry created_at and updated_at: over a join, `SELECT *` returns the
// name twice and the driver keeps the last one, silently shadowing the
// location's own timestamps. None of the option columns above collide with a
// location column.
const LOCATION_WITH_OPTIONS = `(
  SELECT
    location.*,
    ${LOCATION_OPTIONS_COLUMNS.map(column => `options.${column}`).join(',\n    ')}
  FROM avocado.data_location location
  LEFT JOIN avocado.data_location_options options
    ON options.location_id = location.uuid
) AS location_with_options`

// An explicit `tableExpression` still wins — a caller that has gone to the
// trouble of passing one means it.
const optionsTableExpression = (includeOptions, tableExpression) => (
  includeOptions && !tableExpression ? LOCATION_WITH_OPTIONS : tableExpression
)

class LocationPGAdapter extends PGAdapter {
  constructor ({pgConnection, username, logger}) {
    const columns = [
      'fsid',
      'uuid',
      'market',
      'name_of_pharmacy',
      'visit_day',
      'level',
      'classification',
      'zone',
      'city',
      'state',
      'country',
      'lat',
      'long',
      'location_type',
      'active',
      'code',
      'service_fee',
      'supply_weeks',
      'route',
      'mobilized_date',
      'mobilized',
      'address',
      'alt_phone_number',
      'email',
      'notes',
      'owners_name',
      'owners_phone_number',
      'premise_license_number',
      'registration_number',
      'orders_status',
      'superintending_pharmacist',
      'updated_by',
      'company',
      'date_of_first_contact',
      'monthly_revenue_estimate',
      'supply_chain_level',
      'lga'
    ]
    const opts = {
      addUuid: true,
      searchColumns: ['name_of_pharmacy'],
      getRelated: {
        'supplies': {
          relatedTable: 'avocado.data_locationsupply',
          pk: 'uuid',
          fk: 'supply_from_id'
        },
        'supplied_by': {
          relatedTable: 'avocado.data_locationsupply',
          pk: 'uuid',
          fk: 'supply_to_id'
        }
      }
    }

    super(pgConnection, 'avocado.data_location', username, columns, 'uuid', {}, logger, opts)
    this.changes = new LocationChangeAdapter(pgConnection)
  }

  // Opt in to the joined option columns with `includeOptions`. Callers that do
  // not ask keep exactly the payload they get today, which is why this is a
  // parameter rather than the new default.
  async getList ({ includeOptions, ...opts } = {}) {
    return super.getList({
      ...opts,
      tableExpression: optionsTableExpression(includeOptions, opts.tableExpression)
    })
  }

  // Same opt-in for a single location, so a by-id read carries the option
  // columns the list read does.
  async getOne (id, { includeOptions, ...opts } = {}) {
    return super.getOne(id, {
      ...opts,
      tableExpression: optionsTableExpression(includeOptions, opts.tableExpression)
    })
  }

  // Read locations by their couch ids, with the option columns attached.
  // `getByIds`-shaped reads want every match, not a page of them.
  async getByFsidsWithOptions (fsids = []) {
    if (!fsids.length) {
      return []
    }

    const { results } = await this.getList({
      filter: { fsid: fsids },
      includeOptions: true,
      limit: Number.MAX_SAFE_INTEGER,
      skipCount: true,
      // Skips the supplies / supplied_by reads the adapter otherwise fires on
      // every getList: two round trips this caller has no use for.
      getRelated: {}
    })

    return results
  }

  async getOneWithMarketData (id, { whereCondition = this.idColumn, tableExpression } = {}) {
    const query = `SELECT * FROM ${tableExpression || this.tableName} l
        LEFT JOIN "avocado"."data_market" m ON l.market ilike any (m.aliases)
        WHERE ${whereCondition} = $1
    `
    let row
    try {
      const { rows } = await this.pgConnection.query(query, [id])
      row = rows[0]
    } catch (err) {
      if (isPGUsageErrorCode(err.code)) {
        err.status = 400
      }
      throw err
    }
    if (!row) {
      const err = new Error('Row not found')
      err.status = 404
      throw err
    }

    return row
  }

  /**
   * Produce a list of every relationship that supplies from any `from` locations or to any `to` locations.
   * @param from - array of UUIDs
   * @param to - array of UUIDs
   * @returns {Promise<{results: *}>} - returns both UUIDs and FSIDs
   */
  async getBulkLocationsSupply ({ from = [], to = [] }) {
    const text = `
      SELECT supply_from_location.uuid as supply_from_uuid,
             supply_to_location.uuid   as supply_to_uuid,
             supply_from_location.fsid as supply_from_fsid,
             supply_to_location.fsid   as supply_to_fsid
      FROM avocado.data_locationsupply as location_supply
             INNER JOIN avocado.data_location supply_from_location
                        ON supply_from_id = supply_from_location.uuid
             INNER JOIN avocado.data_location supply_to_location
                        ON supply_to_id = supply_to_location.uuid
      WHERE supply_from_id = ANY ($1)
         OR supply_to_id = ANY ($2)
    `

    const values = [from, to]
    let { rows: results } = await this.pgConnection.query(text, values)
    return { results }
  }

  async getBulk (selector = '*', whereCondition = this.idColumn, params = []) {
    selector = Array.isArray(selector) ? selector.join(',') : selector
    const dollarSigns = getPositionalArgs(params)
    const query = `
        SELECT ${selector} FROM ${this.tableName} WHERE ${whereCondition} IN (${dollarSigns})
    `
    const { rows: results } = await this.pgConnection.query(query, [...params])
    return { results }
  }

  async getMarketData (alias) {
    let query = `
        SELECT
            *
        FROM
            "avocado"."data_market" m
    `
    const props = []
    if (alias) {
      query += `WHERE $1 ilike ANY(m.aliases)`
      props.push(alias)
    }
    const { rows } = await this.pgConnection.query(query, props)
    return rows
  }

  async getUserForLocationAndEmail (location, email) {
    const query = `
      SELECT u.name, u.email
      FROM avocado.data_user u
      LEFT JOIN avocado.data_location l ON u.location_id = l.uuid
      WHERE LOWER(u.email) = LOWER($1)
      AND REPLACE(l.name_of_pharmacy, ' ', '') ILIKE $2
    `
    const { rows } = await this.pgConnection.query(query, [email, location])
    return rows
  }

  async getLocationBlockingRule (locationId) {
    const query = `
      SELECT
        "blocking_rule"
      FROM
        "avocado"."data_location_blocking_rules"
      WHERE "location_id" = $1
    `
    const { rows } = await this.pgConnection.query(query, [locationId])
    return rows[0]
  }

  async createLocationBlockingRule ({ locationId, blockingRule }) {
    const query = 'insert into "avocado"."data_location_blocking_rules" (location_id, blocking_rule) values ($1, $2)'
    const { rows } = await this.pgConnection.query(query, [locationId, blockingRule])
    return rows[0]
  }

  async deleteLocationBlockingRule (locationId) {
    const query = `DELETE FROM "avocado"."data_location_blocking_rules" WHERE "location_id" = $1`
    const {rows} = await this.pgConnection.query(query, [locationId])
    return rows
  }

  async getOneByCode (code) {
    const query = `SELECT ${this.columns.join(', ')}, ${this.idColumn} FROM ${this.tableName} WHERE code = $1`
    const { rows } = await this.pgConnection.query(query, [code])

    if (!rows || rows.length === 0) {
      const err = new Error(`No location found with code: ${code}`)
      err.status = 404
      throw err
    }

    return rows[0]
  }

  /**
   * Retrieve multiple locations by UUIDs
   * @param {string[]} uuids
   * @returns {Promise<Object[]>} array of location rows
   */
  async getLocationsByUUIDs (uuids) {
    if (!uuids || uuids.length === 0) return []
    const query = `
      SELECT ${this.columns.join(', ')} FROM ${this.tableName} WHERE uuid = ANY($1)
    `
    const { rows } = await this.pgConnection.query(query, [uuids])
    return rows
  }

  /**
   * Find all active supplier warehouses for a given location and program.
   *
   * Only warehouses with an active `data_location_service` for the requested
   * program are returned (start_date < now, end_date null or in the future).
   *
   * @param {string} locationFsid - FSID of the requesting location (e.g. SDP)
   * @param {string|null} ruleLevel - FSID of the rule scope to match against
   *   (e.g. 'zone:south-south:state:anambra'). Pass null to skip rule-based lookup.
   * @param {string[]} programIds - Array of program FSIDs to filter by
   *   (e.g. ['program:hiv-aids'])
   * @returns {Promise<Array<{uuid: string, fsid: string}>>}
   */
  async getSuppliersForLocation (locationFsid, ruleLevel, programIds) {
    const query = `
      select l.uuid, l.fsid
      from avocado.data_location_relationship_rule rr
      join avocado.data_location l on rr.location_id = l.uuid
      inner join avocado.data_location_service ls on l.uuid = ls.location_id
        and ls.start_date < now()
        and coalesce(ls.end_date, '2099-01-01'::date) > now()
      inner join avocado.data_service s on ls.service_id = s.id
      inner join avocado.data_program p on s.program_id = p.id
      where rr.relationship_rule_type = 'suppliesStateSDPs'
        and rr.relationship_rule = $2
        and p.fsid = any($3)

      union

      select supplier_l.uuid, supplier_l.fsid
      from avocado.data_location sdp_l
      join avocado.data_location_relationship r on sdp_l.uuid = r.location_id
      join avocado.data_location supplier_l on r.relationship = supplier_l.uuid
      inner join avocado.data_location_service ls on supplier_l.uuid = ls.location_id
        and ls.start_date < now()
        and coalesce(ls.end_date, '2099-01-01'::date) > now()
      inner join avocado.data_service s on ls.service_id = s.id
      inner join avocado.data_program p on s.program_id = p.id
      where r.relationship_type = 'suppliedBy'
        and sdp_l.fsid = $1
        and p.fsid = any($3)
    `
    const { rows } = await this.pgConnection.query(query, [locationFsid, ruleLevel, programIds])
    return rows
  }
}

module.exports = {
  LocationPGAdapter
}
